import { z } from 'zod';
import { objectIdSchema } from '../../validation';
import { dateSchema } from '../../validation';

/**
 * Why a captured credit was given back.
 *
 * Only one reason today, but the field is an enum rather than a
 * boolean because a reversal is the kind of thing finance asks
 * questions about a quarter later, and "it has a `reversedAt`" does
 * not survive that conversation.
 */
export const orderSubscriptionReversalReasonSchema = z.enum([
  /** Cancelled inside the grace window with no government waybill issued. */
  'canceled_within_grace',
  /** An operator gave the credit back by hand. */
  'admin_adjustment',
  /**
   * The filing the credit was taken for did not happen — the payload was
   * refused before it left, or Bayan rejected it. On a business metered in
   * documents rather than orders, the credit buys the document, so an
   * attempt that produced none has to give it back or the customer pays
   * for our failure.
   */
  'waybill_not_issued',
]);
export type OrderSubscriptionReversalReason = z.infer<
  typeof orderSubscriptionReversalReasonSchema
>;

/**
 * One subscriber's credit, spent on this order.
 *
 * **This is the ledger.** `Subscription.balance` is a running total
 * derived from these markers, and when the two disagree these win — a
 * capture is two writes to two documents with no transaction between
 * them, so the counter is the side that can drift. Anything that has
 * to be *right* (an audit, a reconciliation, a dispute) counts markers.
 *
 * ## Why an array
 *
 * A single order can be metered by several parties at once. A load
 * moving Customer → Broker → Carrier is one order, one Bayan document —
 * and three separate subscribers, each of whom is using the platform to
 * run their side of it. Charging only the creator would mean a broker
 * pays nothing for every load a customer hands them, which is most of
 * a broker's volume.
 *
 * So each principal captures independently, and each gets its own
 * entry. One entry failing to write does not affect the others: they
 * are separate commercial relationships that happen to share a
 * shipment.
 *
 * ## Why reversal is per-entry
 *
 * The grace window is per-capture, not per-order. An entry written
 * when the broker joined the chain three days after creation has a
 * different clock from the shipper's, and cancelling would return one
 * and not the other.
 */
export const orderSubscriptionCaptureSchema = z.object({
  /** The Subscription the credit came out of. */
  _id: objectIdSchema,
  /**
   * Who owns that subscription — an Org, or a freelancer Driver —
   * denormalised so "has this party already paid for this order?" is
   * answerable without loading every subscription referenced by the
   * array. That question runs on the capture path itself, where it is
   * the idempotency check.
   */
  owner: objectIdSchema,
  /**
   * When the credit was taken. Also the start of the reversal window —
   * see `SUBSCRIPTION_CONFIG.usage.reversalGraceHours`.
   */
  capturedAt: dateSchema,
  /**
   * Which `free` grant this capture drew from, when it spent a gifted
   * credit rather than a purchased one — see
   * `subscriptionGrantSchema`. Absent means the credit came from the
   * purchased balance or the repayable overdraft. Lets
   * `SubscriptionCreditsService.reconcile` attribute consumption back to
   * a grant so expiry/revoke only ever reclaims *unspent* gifts.
   */
  grantId: objectIdSchema.optional(),
  /**
   * Set when the credit was handed back; the entry is kept rather than
   * pulled so the history of "charged, then refunded" survives, and so
   * a re-cancel cannot double-refund.
   */
  reversedAt: dateSchema.optional(),
  reversalReason: orderSubscriptionReversalReasonSchema.optional(),
});
export type OrderSubscriptionCapture = z.infer<typeof orderSubscriptionCaptureSchema>;
